import React, { createContext, useContext, useState, useEffect } from 'react';

// Theme definitions
const themes = {
  // Base themes
  light: {
    id: 'light',
    name: 'Light',
    category: 'base',
    colors: {
      background: '#ffffff',
      foreground: '#0f172a',
      card: '#ffffff',
      cardForeground: '#0f172a',
      primary: '#3b82f6',
      primaryForeground: '#ffffff',
      secondary: '#f1f5f9',
      secondaryForeground: '#0f172a',
      muted: '#f1f5f9',
      mutedForeground: '#64748b',
      accent: '#f1f5f9',
      accentForeground: '#0f172a',
      destructive: '#ef4444',
      destructiveForeground: '#ffffff',
      border: '#e2e8f0',
      input: '#e2e8f0',
      ring: '#3b82f6',
      sidebar: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
      sidebarText: '#ffffff',
    }
  },
  dark: {
    id: 'dark',
    name: 'Dark',
    category: 'base',
    colors: {
      background: '#0f172a',
      foreground: '#f8fafc',
      card: '#1e293b',
      cardForeground: '#f8fafc',
      primary: '#3b82f6',
      primaryForeground: '#ffffff',
      secondary: '#1e293b',
      secondaryForeground: '#f8fafc',
      muted: '#1e293b',
      mutedForeground: '#94a3b8',
      accent: '#1e293b',
      accentForeground: '#f8fafc',
      destructive: '#ef4444',
      destructiveForeground: '#ffffff',
      border: '#334155',
      input: '#334155',
      ring: '#3b82f6',
      sidebar: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      sidebarText: '#ffffff',
    }
  },

  // Star Wars themes
  mandalorian: {
    id: 'mandalorian',
    name: 'Mandalorian',
    category: 'starwars',
    icon: '🪖',
    colors: {
      background: '#1a1a2e',
      foreground: '#e8e8e8',
      card: '#16213e',
      cardForeground: '#e8e8e8',
      primary: '#c9a227',
      primaryForeground: '#000000',
      secondary: '#0f3460',
      secondaryForeground: '#e8e8e8',
      muted: '#0f3460',
      mutedForeground: '#9ca3af',
      accent: '#c9a227',
      accentForeground: '#000000',
      destructive: '#e94560',
      destructiveForeground: '#ffffff',
      border: '#374151',
      input: '#374151',
      ring: '#c9a227',
      sidebar: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      sidebarText: '#c9a227',
    }
  },
  empire: {
    id: 'empire',
    name: 'Galactic Empire',
    category: 'starwars',
    icon: '⚫',
    colors: {
      background: '#0d0d0d',
      foreground: '#e0e0e0',
      card: '#1a1a1a',
      cardForeground: '#e0e0e0',
      primary: '#cc0000',
      primaryForeground: '#ffffff',
      secondary: '#262626',
      secondaryForeground: '#e0e0e0',
      muted: '#262626',
      mutedForeground: '#9ca3af',
      accent: '#cc0000',
      accentForeground: '#ffffff',
      destructive: '#ff0000',
      destructiveForeground: '#ffffff',
      border: '#404040',
      input: '#404040',
      ring: '#cc0000',
      sidebar: 'linear-gradient(135deg, #0d0d0d 0%, #1a1a1a 50%, #262626 100%)',
      sidebarText: '#cc0000',
    }
  },
  jedi: {
    id: 'jedi',
    name: 'Jedi Temple',
    category: 'starwars',
    icon: '⚔️',
    colors: {
      background: '#f5f0e6',
      foreground: '#2d2d2d',
      card: '#ffffff',
      cardForeground: '#2d2d2d',
      primary: '#2e7d32',
      primaryForeground: '#ffffff',
      secondary: '#e8e0d0',
      secondaryForeground: '#2d2d2d',
      muted: '#e8e0d0',
      mutedForeground: '#666666',
      accent: '#1565c0',
      accentForeground: '#ffffff',
      destructive: '#c62828',
      destructiveForeground: '#ffffff',
      border: '#d4c9b8',
      input: '#d4c9b8',
      ring: '#2e7d32',
      sidebar: 'linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 50%, #4d4d4d 100%)',
      sidebarText: '#a8e6cf',
    }
  },

  // Big Bang Theory themes
  bazinga: {
    id: 'bazinga',
    name: 'Bazinga!',
    category: 'bigbang',
    icon: '🔬',
    colors: {
      background: '#fff8e1',
      foreground: '#212121',
      card: '#ffffff',
      cardForeground: '#212121',
      primary: '#ff5722',
      primaryForeground: '#ffffff',
      secondary: '#ffecb3',
      secondaryForeground: '#212121',
      muted: '#ffecb3',
      mutedForeground: '#666666',
      accent: '#2196f3',
      accentForeground: '#ffffff',
      destructive: '#f44336',
      destructiveForeground: '#ffffff',
      border: '#ffe082',
      input: '#ffe082',
      ring: '#ff5722',
      sidebar: 'linear-gradient(135deg, #d32f2f 0%, #f44336 50%, #ff5722 100%)',
      sidebarText: '#ffffff',
    }
  },
  physicist: {
    id: 'physicist',
    name: 'Theoretical Physics',
    category: 'bigbang',
    icon: '🧪',
    colors: {
      background: '#eceff1',
      foreground: '#263238',
      card: '#ffffff',
      cardForeground: '#263238',
      primary: '#00897b',
      primaryForeground: '#ffffff',
      secondary: '#cfd8dc',
      secondaryForeground: '#263238',
      muted: '#cfd8dc',
      mutedForeground: '#607d8b',
      accent: '#7c4dff',
      accentForeground: '#ffffff',
      destructive: '#d32f2f',
      destructiveForeground: '#ffffff',
      border: '#b0bec5',
      input: '#b0bec5',
      ring: '#00897b',
      sidebar: 'linear-gradient(135deg, #263238 0%, #37474f 50%, #455a64 100%)',
      sidebarText: '#80cbc4',
    }
  },
  nerds: {
    id: 'nerds',
    name: 'Comic Book Store',
    category: 'bigbang',
    icon: '📚',
    colors: {
      background: '#fce4ec',
      foreground: '#212121',
      card: '#ffffff',
      cardForeground: '#212121',
      primary: '#e91e63',
      primaryForeground: '#ffffff',
      secondary: '#f8bbd0',
      secondaryForeground: '#212121',
      muted: '#f8bbd0',
      mutedForeground: '#666666',
      accent: '#9c27b0',
      accentForeground: '#ffffff',
      destructive: '#f44336',
      destructiveForeground: '#ffffff',
      border: '#f48fb1',
      input: '#f48fb1',
      ring: '#e91e63',
      sidebar: 'linear-gradient(135deg, #4a148c 0%, #6a1b9a 50%, #7b1fa2 100%)',
      sidebarText: '#f8bbd0',
    }
  },

  // Marvel themes
  ironman: {
    id: 'ironman',
    name: 'Iron Man',
    category: 'marvel',
    icon: '🦾',
    colors: {
      background: '#1a1a1a',
      foreground: '#ffffff',
      card: '#2d2d2d',
      cardForeground: '#ffffff',
      primary: '#c41e3a',
      primaryForeground: '#ffd700',
      secondary: '#404040',
      secondaryForeground: '#ffffff',
      muted: '#404040',
      mutedForeground: '#a0a0a0',
      accent: '#ffd700',
      accentForeground: '#1a1a1a',
      destructive: '#ff0000',
      destructiveForeground: '#ffffff',
      border: '#555555',
      input: '#555555',
      ring: '#ffd700',
      sidebar: 'linear-gradient(135deg, #8b0000 0%, #c41e3a 50%, #ffd700 100%)',
      sidebarText: '#ffd700',
    }
  },
  captainamerica: {
    id: 'captainamerica',
    name: 'Captain America',
    category: 'marvel',
    icon: '🛡️',
    colors: {
      background: '#f5f5f5',
      foreground: '#1a237e',
      card: '#ffffff',
      cardForeground: '#1a237e',
      primary: '#1a237e',
      primaryForeground: '#ffffff',
      secondary: '#e3f2fd',
      secondaryForeground: '#1a237e',
      muted: '#e3f2fd',
      mutedForeground: '#5c6bc0',
      accent: '#c62828',
      accentForeground: '#ffffff',
      destructive: '#c62828',
      destructiveForeground: '#ffffff',
      border: '#bbdefb',
      input: '#bbdefb',
      ring: '#1a237e',
      sidebar: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)',
      sidebarText: '#ffffff',
    }
  },
  spiderman: {
    id: 'spiderman',
    name: 'Spider-Man',
    category: 'marvel',
    icon: '🕷️',
    colors: {
      background: '#fff3e0',
      foreground: '#b71c1c',
      card: '#ffffff',
      cardForeground: '#b71c1c',
      primary: '#b71c1c',
      primaryForeground: '#ffffff',
      secondary: '#ffccbc',
      secondaryForeground: '#b71c1c',
      muted: '#ffccbc',
      mutedForeground: '#d32f2f',
      accent: '#1565c0',
      accentForeground: '#ffffff',
      destructive: '#d32f2f',
      destructiveForeground: '#ffffff',
      border: '#ffab91',
      input: '#ffab91',
      ring: '#b71c1c',
      sidebar: 'linear-gradient(135deg, #b71c1c 0%, #c62828 50%, #d32f2f 100%)',
      sidebarText: '#ffffff',
    }
  },

  // DC themes
  batman: {
    id: 'batman',
    name: 'Batman',
    category: 'dc',
    icon: '🦇',
    colors: {
      background: '#0a0a0a',
      foreground: '#e0e0e0',
      card: '#1a1a1a',
      cardForeground: '#e0e0e0',
      primary: '#1a1a1a',
      primaryForeground: '#ffd700',
      secondary: '#2a2a2a',
      secondaryForeground: '#e0e0e0',
      muted: '#2a2a2a',
      mutedForeground: '#808080',
      accent: '#ffd700',
      accentForeground: '#0a0a0a',
      destructive: '#8b0000',
      destructiveForeground: '#ffffff',
      border: '#404040',
      input: '#404040',
      ring: '#ffd700',
      sidebar: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #2a2a2a 100%)',
      sidebarText: '#ffd700',
    }
  },
  superman: {
    id: 'superman',
    name: 'Superman',
    category: 'dc',
    icon: '🦸',
    colors: {
      background: '#e3f2fd',
      foreground: '#0d47a1',
      card: '#ffffff',
      cardForeground: '#0d47a1',
      primary: '#0d47a1',
      primaryForeground: '#ffffff',
      secondary: '#bbdefb',
      secondaryForeground: '#0d47a1',
      muted: '#bbdefb',
      mutedForeground: '#1976d2',
      accent: '#c62828',
      accentForeground: '#ffffff',
      destructive: '#c62828',
      destructiveForeground: '#ffffff',
      border: '#90caf9',
      input: '#90caf9',
      ring: '#0d47a1',
      sidebar: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 50%, #1976d2 100%)',
      sidebarText: '#ffd700',
    }
  },
  joker: {
    id: 'joker',
    name: 'Joker',
    category: 'dc',
    icon: '🃏',
    colors: {
      background: '#1a0a2e',
      foreground: '#e0e0e0',
      card: '#2d1b4e',
      cardForeground: '#e0e0e0',
      primary: '#7b1fa2',
      primaryForeground: '#ffffff',
      secondary: '#4a148c',
      secondaryForeground: '#e0e0e0',
      muted: '#4a148c',
      mutedForeground: '#ba68c8',
      accent: '#00e676',
      accentForeground: '#1a0a2e',
      destructive: '#ff1744',
      destructiveForeground: '#ffffff',
      border: '#6a1b9a',
      input: '#6a1b9a',
      ring: '#00e676',
      sidebar: 'linear-gradient(135deg, #4a148c 0%, #6a1b9a 50%, #7b1fa2 100%)',
      sidebarText: '#00e676',
    }
  },
};

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem('app-theme');
    return saved || 'light';
  });

  useEffect(() => {
    const theme = themes[currentTheme];
    if (!theme) return;

    const root = document.documentElement;
    const colors = theme.colors;

    // Apply CSS variables
    root.style.setProperty('--background', colors.background);
    root.style.setProperty('--foreground', colors.foreground);
    root.style.setProperty('--card', colors.card);
    root.style.setProperty('--card-foreground', colors.cardForeground);
    root.style.setProperty('--primary', colors.primary);
    root.style.setProperty('--primary-foreground', colors.primaryForeground);
    root.style.setProperty('--secondary', colors.secondary);
    root.style.setProperty('--secondary-foreground', colors.secondaryForeground);
    root.style.setProperty('--muted', colors.muted);
    root.style.setProperty('--muted-foreground', colors.mutedForeground);
    root.style.setProperty('--accent', colors.accent);
    root.style.setProperty('--accent-foreground', colors.accentForeground);
    root.style.setProperty('--destructive', colors.destructive);
    root.style.setProperty('--destructive-foreground', colors.destructiveForeground);
    root.style.setProperty('--border', colors.border);
    root.style.setProperty('--input', colors.input);
    root.style.setProperty('--ring', colors.ring);
    root.style.setProperty('--sidebar-gradient', colors.sidebar);
    root.style.setProperty('--sidebar-text', colors.sidebarText);

    // Apply body background
    document.body.style.backgroundColor = colors.background;
    document.body.style.color = colors.foreground;

    // Save to localStorage
    localStorage.setItem('app-theme', currentTheme);
  }, [currentTheme]);

  const setTheme = (themeId) => {
    if (themes[themeId]) {
      setCurrentTheme(themeId);
    }
  };

  const getThemesByCategory = (category) => {
    return Object.values(themes).filter(t => t.category === category);
  };

  const value = {
    currentTheme,
    setTheme,
    theme: themes[currentTheme],
    themes,
    getThemesByCategory,
    isDark: ['dark', 'mandalorian', 'empire', 'ironman', 'batman', 'joker'].includes(currentTheme),
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default themes;
